'use client'

/**
 * <FormRenderer />
 *
 * Schema-driven runtime for v2 (Puck) forms. Reads the schema, evaluates
 * `showIf` reactively, splits content on PageBreak blocks for multi-page
 * navigation, validates per-type, and submits the payload.
 *
 * v1 covers the public-link surface. The recipient/signing-token path
 * (/forms/sign/:token) is not wired through this renderer yet — see plan
 * "Deferred to Follow-Up Work".
 */

import { useEffect, useMemo, useRef, useState, type ComponentType } from 'react'
import type { FormBlock, FormSchema } from '@school-journey/shared-types/schemas'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import {
  CheckboxField,
  DateField,
  EmailField,
  LongTextField,
  MarkdownField,
  MultiSelectField,
  PhoneField,
  SectionHeaderField,
  SelectField,
  ShortTextField,
  SignatureField
} from '@/components/form-fields'
import { computeVisibleBlockIds, splitSchemaIntoPages } from './show-if'
import { validateBlock } from './validation'

/** Signer identity captured alongside a signature when the block requires it. */
interface SignerIdentity {
  name: string
  email: string
}

interface FormRendererProps {
  schema: FormSchema
  title: string
  description?: string | null
  schoolName?: string | null
  onSubmit: (
    payload: Record<string, unknown>,
    signature?: { type: 'TYPED'; text: string },
    signerIdentity?: SignerIdentity
  ) => Promise<void>
  submitting?: boolean
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

type SignatureResolution =
  | { ok: true; signature?: { type: 'TYPED'; text: string }; signerIdentity?: SignerIdentity; consumedId?: string }
  | { ok: false; signerErrors: { name?: string; email?: string } }

/** Pull the typed signature (if present + visible) out of the payload. */
function extractTypedSignature(
  signatureBlock: FormBlock | undefined,
  visibleIds: Set<string>,
  payload: Record<string, unknown>
): { signature?: { type: 'TYPED'; text: string }; consumedId?: string; visible: boolean } {
  if (!signatureBlock || !('id' in signatureBlock.props)) return { visible: false }
  const id = signatureBlock.props.id
  const visible = visibleIds.has(id)
  const sigVal = payload[id]
  if (typeof sigVal === 'string' && sigVal.length > 0) {
    return { signature: { type: 'TYPED', text: sigVal }, consumedId: id, visible }
  }
  return { visible }
}

/** Validate the required signer name + email (R7). */
function validateSignerIdentity(
  signerName: string,
  signerEmail: string
): { ok: true; identity: SignerIdentity } | { ok: false; errors: { name?: string; email?: string } } {
  const name = signerName.trim()
  const email = signerEmail.trim()
  const errors: { name?: string; email?: string } = {}
  if (!name) errors.name = 'Signer name is required'
  if (!email) errors.email = 'Signer email is required'
  else if (!EMAIL_RE.test(email)) errors.email = 'Enter a valid email'
  if (errors.name || errors.email) return { ok: false, errors }
  return { ok: true, identity: { name, email } }
}

/**
 * Resolve the typed signature and, when the block requires it, the signer
 * identity. Kept out of `handleSubmit` so the submit path stays flat.
 */
function resolveSignatureSubmission(args: {
  signatureBlock: FormBlock | undefined
  visibleIds: Set<string>
  payload: Record<string, unknown>
  requireIdentity: boolean
  signerName: string
  signerEmail: string
}): SignatureResolution {
  const { signatureBlock, visibleIds, payload, requireIdentity, signerName, signerEmail } = args
  const { signature, consumedId, visible } = extractTypedSignature(signatureBlock, visibleIds, payload)
  if (!requireIdentity || !visible) return { ok: true, signature, consumedId }

  const identity = validateSignerIdentity(signerName, signerEmail)
  if (!identity.ok) return { ok: false, signerErrors: identity.errors }
  return { ok: true, signature, signerIdentity: identity.identity, consumedId }
}

export function FormRenderer({ schema, title, description, schoolName, onSubmit, submitting }: FormRendererProps) {
  const pages = useMemo(() => splitSchemaIntoPages(schema), [schema])
  const [pageIndex, setPageIndex] = useState(0)
  const [direction, setDirection] = useState<'forward' | 'back'>('forward')
  const [payload, setPayload] = useState<Record<string, unknown>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [honeypot, setHoneypot] = useState('')
  const [signerName, setSignerName] = useState('')
  const [signerEmail, setSignerEmail] = useState('')
  const [signerErrors, setSignerErrors] = useState<{ name?: string; email?: string }>({})

  // A form is consent-bearing when it carries a signature; identity capture is
  // governed by the block's `requireSignerIdentity` prop (R5/R7).
  const signatureBlock = useMemo(() => schema.content.find(b => b.type === 'Signature'), [schema])
  const requireIdentity = signatureBlock?.type === 'Signature' && Boolean(signatureBlock.props.requireSignerIdentity)

  const visibleIds = useMemo(() => computeVisibleBlockIds(schema, payload), [schema, payload])
  const isLastPage = pageIndex === pages.length - 1
  const currentPage = pages[pageIndex]

  // Reentrancy guards: the `submitting` prop only gates after the parent
  // re-renders, so a rapid double-click could double-submit or skip a page.
  // navLock releases once the page settles; submitLock around the async submit.
  const navLockRef = useRef(false)
  const submitLockRef = useRef(false)
  useEffect(() => {
    navLockRef.current = false
  }, [pageIndex])

  function setFieldValue(id: string, value: unknown) {
    setPayload(prev => ({ ...prev, [id]: value }))
    if (errors[id]) setErrors(({ [id]: _ignored, ...rest }) => rest)
  }

  function validateCurrentPage(): boolean {
    const next: Record<string, string> = {}
    for (const block of currentPage.blocks) {
      if ('id' in block.props && !visibleIds.has(block.props.id)) continue
      const error = validateBlock(block, 'id' in block.props ? payload[block.props.id] : undefined)
      if (error) next[error.fieldId] = error.message
    }
    setErrors(next)
    return Object.keys(next).length === 0
  }

  async function handleNext() {
    if (navLockRef.current) return
    if (!validateCurrentPage()) return
    navLockRef.current = true
    setDirection('forward')
    setPageIndex(i => Math.min(i + 1, pages.length - 1))
  }

  function handleBack() {
    if (navLockRef.current) return
    navLockRef.current = true
    setDirection('back')
    setPageIndex(i => Math.max(0, i - 1))
  }

  async function handleSubmit() {
    if (submitLockRef.current) return
    if (!validateCurrentPage()) return
    // Strip hidden fields from the payload before submit (matches backend behavior).
    const sanitized: Record<string, unknown> = {}
    for (const id of visibleIds) {
      if (payload[id] !== undefined) sanitized[id] = payload[id]
    }
    // Resolve the typed signature + required signer identity (R7). Identity is
    // validated only when the requiring block is on-screen; the backend
    // re-enforces for consent-bearing forms.
    const resolved = resolveSignatureSubmission({
      signatureBlock,
      visibleIds,
      payload,
      requireIdentity,
      signerName,
      signerEmail
    })
    if (!resolved.ok) {
      setSignerErrors(resolved.signerErrors)
      return
    }
    if (resolved.consumedId) delete sanitized[resolved.consumedId]

    submitLockRef.current = true
    try {
      await onSubmit(
        { ...sanitized, ...(honeypot ? { _hp: honeypot } : {}) },
        resolved.signature,
        resolved.signerIdentity
      )
    } finally {
      submitLockRef.current = false
    }
  }

  const progressPct = pages.length > 1 ? ((pageIndex + 1) / pages.length) * 100 : 0

  return (
    // Premium form shell (U2): calm reading column, full-bleed on mobile and a
    // card on desktop. Mobile keeps no card per spec; the 16px input text guards
    // against iOS zoom. The shared field components carry the field-level uplift.
    <div className="mx-auto w-full max-w-xl space-y-6 px-4 py-6 sm:rounded-xl sm:border sm:border-border sm:bg-card sm:px-8 sm:py-10 sm:shadow-xs">
      <header className="space-y-1.5">
        {schoolName && <p className="text-sm text-muted-foreground">{schoolName}</p>}
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        {description && <p className="text-base text-muted-foreground">{description}</p>}
        {pages.length > 1 && (
          <div className="space-y-1.5 pt-1">
            <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-primary transition-all duration-300"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <p className="text-sm text-muted-foreground">
              Step {pageIndex + 1} of {pages.length}
              {currentPage.title ? ` — ${currentPage.title}` : ''}
            </p>
          </div>
        )}
      </header>

      {/* Honeypot field: visually hidden, real users don't fill it. */}
      <input
        type="text"
        autoComplete="off"
        tabIndex={-1}
        value={honeypot}
        onChange={e => setHoneypot(e.target.value)}
        style={{ position: 'absolute', left: '-9999px', height: 0, width: 0, opacity: 0 }}
        aria-hidden="true"
      />

      {/* Keyed by pageIndex so each step remounts and replays the transition. */}
      <div
        key={pageIndex}
        className={cn(
          'space-y-6 duration-200 animate-in fade-in',
          direction === 'forward' ? 'slide-in-from-bottom-1' : 'slide-in-from-top-1'
        )}
      >
        {currentPage.blocks.map(block => {
          // Signature is composite (typed value + optional signer identity), so
          // it renders here with access to component state; other blocks route
          // through the stateless `renderBlock`.
          if (block.type === 'Signature') {
            const sigId = block.props.id
            if (!visibleIds.has(sigId)) return null
            return (
              <SignatureField
                key={sigId}
                props={block.props}
                mode="live"
                value={payload[sigId] as string}
                onChange={v => setFieldValue(sigId, v)}
                error={errors[sigId]}
                signerName={signerName}
                signerEmail={signerEmail}
                onSignerNameChange={v => {
                  setSignerName(v)
                  if (signerErrors.name) setSignerErrors(prev => ({ ...prev, name: undefined }))
                }}
                onSignerEmailChange={v => {
                  setSignerEmail(v)
                  if (signerErrors.email) setSignerErrors(prev => ({ ...prev, email: undefined }))
                }}
                signerNameError={signerErrors.name}
                signerEmailError={signerErrors.email}
              />
            )
          }
          return renderBlock(block, payload, visibleIds, errors, setFieldValue)
        })}
      </div>

      <footer className="flex items-center justify-between border-t pt-4">
        <Button type="button" variant="outline" onClick={handleBack} disabled={pageIndex === 0 || submitting}>
          Back
        </Button>
        {isLastPage ? (
          <Button type="button" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Submitting…' : 'Submit'}
          </Button>
        ) : (
          <Button type="button" onClick={handleNext} disabled={submitting}>
            Next
          </Button>
        )}
      </footer>
    </div>
  )
}

// Shared live-mode field shape — every answerable `form-fields/*` component
// accepts it and narrows its own value internally, so one loosely-typed lookup
// replaces the per-type render switch.
type LiveAnswerableField = ComponentType<{
  props: unknown
  mode: 'live'
  value: unknown
  onChange: (value: unknown) => void
  error?: string
}>

const ANSWERABLE_FIELDS = {
  ShortText: ShortTextField,
  Email: EmailField,
  Phone: PhoneField,
  LongText: LongTextField,
  Date: DateField,
  Checkbox: CheckboxField,
  Select: SelectField,
  MultiSelect: MultiSelectField
} as unknown as Record<string, LiveAnswerableField | undefined>

function renderBlock(
  block: FormBlock,
  payload: Record<string, unknown>,
  visibleIds: Set<string>,
  errors: Record<string, string>,
  setValue: (id: string, value: unknown) => void
) {
  // Decoration blocks render via the shared components (live = preview here).
  if (block.type === 'PageBreak') return null
  if (block.type === 'SectionHeader') {
    return <SectionHeaderField key={block.props.id} props={block.props} mode="live" />
  }
  if (block.type === 'Markdown') {
    return <MarkdownField key={block.props.id} props={block.props} mode="live" />
  }

  const id = block.props.id
  if (!visibleIds.has(id)) return null
  const value = payload[id]
  const error = errors[id]

  // Answerable fields delegate to the shared `form-fields/*` components in
  // `live` mode — the SAME components the Puck builder renders in `preview`. A
  // lookup keeps this flat; each component internally narrows its own value.
  const Field = ANSWERABLE_FIELDS[block.type]
  if (Field) {
    return (
      <Field key={id} props={block.props} mode="live" value={value} onChange={v => setValue(id, v)} error={error} />
    )
  }

  // Deferred stubs (FileUpload / RepeatingSection) — Signature is now live
  // (handled by the caller).
  return renderDeferredStub(block, id, error)
}

/** Placeholder render for the not-yet-activated FileUpload / RepeatingSection blocks. */
function renderDeferredStub(block: FormBlock, id: string, error: string | undefined) {
  // Only these two reach here (all others handled above); narrowing lets us read
  // BlockBaseProps directly without per-prop `in` guards.
  if (block.type !== 'FileUpload' && block.type !== 'RepeatingSection') return null
  const { label, required, helpText } = block.props
  const labelEl = (
    <Label htmlFor={id} className="block">
      {label}
      {required && <span className="ml-1 text-destructive">*</span>}
    </Label>
  )
  const placeholder = block.type === 'RepeatingSection' ? 'Repeating sections coming soon' : 'File upload coming soon'
  return (
    <div key={id} className="space-y-1 rounded border p-3">
      {labelEl}
      <p className="text-xs text-muted-foreground">{placeholder}</p>
      {helpText ? <p className="text-xs text-muted-foreground">{helpText}</p> : null}
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  )
}
